import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MarketingPromotion.css';

const MarketingPromotion = () => {
  const { t } = useTranslation('navigation');
  const [activeTab, setActiveTab] = useState('campaign-manager');
  
  // Helper function to translate campaign status
  const translateCampaignStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'live': t('marketing.campaigns.status.live'),
      'scheduled': t('marketing.campaigns.status.scheduled'),
      'draft': t('marketing.campaigns.status.draft'),
      'active': t('marketing.campaigns.status.active'),
      'paused': t('marketing.campaigns.status.paused')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [showCreateCouponModal, setShowCreateCouponModal] = useState(false);
  const [showCreateAutomationModal, setShowCreateAutomationModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    description: '',
    type: 'email',
    channels: ['email'],
    content: '',
    targetSegment: '',
    scheduleDate: '',
    sendImmediately: false,
    utmTracking: false
  });

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    expiresAt: '',
    maxUses: '',
    targetAudience: 'all'
  });

  const [newAutomation, setNewAutomation] = useState({
    name: '',
    trigger: '',
    conditions: [],
    actions: [],
    delay: 0
  });

  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      title: 'Summer School Promo',
      type: 'Email + Push',
      status: 'live',
      startDate: '2025-07-01',
      reach: 12400,
      openRate: 23.5,
      clickRate: 8.2,
      conversionRate: 3.1
    },
    {
      id: 2,
      title: 'Back to School Offer',
      type: 'SMS',
      status: 'scheduled',
      startDate: '2025-08-15',
      reach: 6020,
      openRate: 0,
      clickRate: 0,
      conversionRate: 0
    },
    {
      id: 3,
      title: 'New Article Alert',
      type: 'Email',
      status: 'draft',
      startDate: '',
      reach: 0,
      openRate: 0,
      clickRate: 0,
      conversionRate: 0
    }
  ]);

  const [audienceSegments, setAudienceSegments] = useState([
    {
      id: 1,
      name: 'Active High School Students',
      filters: [
        { type: 'userType', value: 'student' },
        { type: 'engagement', value: 'active_daily' },
        { type: 'region', value: 'algiers' }
      ],
      count: 2340,
      lastUpdated: '2025-01-15'
    },
    {
      id: 2,
      name: 'Inactive Free Users',
      filters: [
        { type: 'subscription', value: 'free' },
        { type: 'engagement', value: 'inactive_7_days' }
      ],
      count: 1560,
      lastUpdated: '2025-01-14'
    },
    {
      id: 3,
      name: 'Premium Teachers',
      filters: [
        { type: 'userType', value: 'teacher' },
        { type: 'subscription', value: 'pro' }
      ],
      count: 890,
      lastUpdated: '2025-01-13'
    }
  ]);

  const [coupons, setCoupons] = useState([
    {
      id: 1,
      code: 'NATI10',
      discountType: 'percentage',
      discountValue: 10,
      expiresAt: '2025-08-01',
      uses: 5,
      maxUses: 100,
      status: 'active',
      redemptions: 5,
      conversionRate: 12.5
    },
    {
      id: 2,
      code: 'BACK2SCHOOL',
      discountType: 'feature',
      discountValue: 'Free 1-month upgrade',
      expiresAt: '2025-09-10',
      uses: 45,
      maxUses: -1,
      status: 'active',
      redemptions: 45,
      conversionRate: 8.3
    }
  ]);

  const [automationRules, setAutomationRules] = useState([
    {
      id: 1,
      name: 'Inactive User Reminder',
      trigger: 'user_inactive_7_days',
      actions: ['send_sms', 'in_app_nudge'],
      status: 'active',
      lastTriggered: '2025-01-15',
      totalTriggers: 234
    },
    {
      id: 2,
      name: 'Article Completion Reward',
      trigger: 'student_completes_5_articles',
      actions: ['send_upgrade_coupon'],
      status: 'active',
      lastTriggered: '2025-01-14',
      totalTriggers: 89
    }
  ]);

  const [analytics, setAnalytics] = useState({
    totalCampaigns: 15,
    activeCampaigns: 3,
    totalReach: 45600,
    avgOpenRate: 24.3,
    avgClickRate: 7.8,
    avgConversionRate: 2.9,
    topPerformingCampaign: 'Summer School Promo',
    bestSendingTime: 'Tuesday 10:00 AM',
    deviceBreakdown: {
      mobile: 68,
      desktop: 32
    }
  });

  const tabs = [
    { id: 'campaign-manager', label: t('marketing.tabs.campaignManager'), icon: 'campaign' },
    { id: 'audience-targeting', label: t('marketing.tabs.audienceTargeting'), icon: 'target' },
    { id: 'email-sms-blasts', label: t('marketing.tabs.emailSms'), icon: 'email' },
    { id: 'coupons-offers', label: t('marketing.tabs.couponsOffers'), icon: 'local_offer' },
    { id: 'campaign-analytics', label: t('marketing.tabs.analytics'), icon: 'analytics' },
    { id: 'automation-rules', label: t('marketing.tabs.automation'), icon: 'auto_awesome' }
  ];

  const handleCreateCampaign = () => {
    if (newCampaign.title && newCampaign.content) {
      const campaign = {
        id: Date.now(),
        title: newCampaign.title,
        type: newCampaign.channels.join(' + '),
        status: newCampaign.sendImmediately ? 'live' : 'draft',
        startDate: newCampaign.sendImmediately ? new Date().toISOString().split('T')[0] : newCampaign.scheduleDate,
        reach: 0,
        openRate: 0,
        clickRate: 0,
        conversionRate: 0
      };
      setCampaigns([...campaigns, campaign]);
      setNewCampaign({
        title: '',
        description: '',
        type: 'email',
        channels: ['email'],
        content: '',
        targetSegment: '',
        scheduleDate: '',
        sendImmediately: false,
        utmTracking: false
      });
      setShowCreateCampaignModal(false);
    }
  };

  const handleCreateCoupon = () => {
    if (newCoupon.code && newCoupon.discountValue) {
      const coupon = {
        id: Date.now(),
        code: newCoupon.code,
        discountType: newCoupon.discountType,
        discountValue: newCoupon.discountValue,
        expiresAt: newCoupon.expiresAt,
        uses: 0,
        maxUses: newCoupon.maxUses === '' ? -1 : parseInt(newCoupon.maxUses),
        status: 'active',
        redemptions: 0,
        conversionRate: 0
      };
      setCoupons([...coupons, coupon]);
      setNewCoupon({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        expiresAt: '',
        maxUses: '',
        targetAudience: 'all'
      });
      setShowCreateCouponModal(false);
    }
  };

  const renderCampaignManager = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('marketing.campaigns.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button 
          className="add-user-btn"
          onClick={() => setShowCreateCampaignModal(true)}
        >
          <span className="material-icons">add</span>
          {t('marketing.campaigns.createCampaign')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('marketing.campaigns.tableHeaders.id')}</th>
              <th>{t('marketing.campaigns.tableHeaders.campaign')}</th>
              <th>{t('marketing.campaigns.tableHeaders.type')}</th>
              <th>{t('marketing.campaigns.tableHeaders.status')}</th>
              <th>{t('marketing.campaigns.tableHeaders.startDate')}</th>
              <th>{t('marketing.campaigns.tableHeaders.reach')}</th>
              <th>{t('marketing.campaigns.tableHeaders.performance')}</th>
              <th>{t('marketing.campaigns.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr key={campaign.id}>
                <td>{campaign.id}</td>
                <td>{campaign.title}</td>
                <td>
                  <span className="campaign-type">{campaign.type}</span>
                </td>
                <td>
                  <span className={`status-badge ${campaign.status}`}>
                    {translateCampaignStatus(campaign.status)}
                  </span>
                </td>
                <td>{campaign.startDate || t('marketing.campaigns.status.notSet')}</td>
                <td>{campaign.reach.toLocaleString()}</td>
                <td>
                  <div className="performance-metrics">
                    <span>{t('marketing.campaigns.performance.open')}: {campaign.openRate}%</span>
                    <span>{t('marketing.campaigns.performance.click')}: {campaign.clickRate}%</span>
                    <span>{t('marketing.campaigns.performance.conv')}: {campaign.conversionRate}%</span>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('marketing.campaigns.actions.view')}>
                      <span className="material-icons">visibility</span>
                    </button>
                    <button className="action-btn edit" title={t('marketing.campaigns.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    {campaign.status === 'live' && (
                      <button className="action-btn toggle" title={t('marketing.campaigns.actions.pause')}>
                        <span className="material-icons">pause</span>
                      </button>
                    )}
                    <button className="action-btn delete" title={t('marketing.campaigns.actions.delete')}>
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAudienceTargeting = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('marketing.audience.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button className="add-user-btn">
          <span className="material-icons">add</span>
          {t('marketing.audience.createSegment')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('marketing.audience.tableHeaders.id')}</th>
              <th>{t('marketing.audience.tableHeaders.segmentName')}</th>
              <th>{t('marketing.audience.tableHeaders.filters')}</th>
              <th>{t('marketing.audience.tableHeaders.userCount')}</th>
              <th>{t('marketing.audience.tableHeaders.lastUpdated')}</th>
              <th>{t('marketing.audience.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {audienceSegments.map((segment) => (
              <tr key={segment.id}>
                <td>{segment.id}</td>
                <td>{segment.name}</td>
                <td>
                  <div className="segment-filters">
                    {segment.filters.map((filter, index) => (
                      <span key={index} className="filter-badge">
                        {filter.type}: {filter.value}
                      </span>
                    ))}
                  </div>
                </td>
                <td>{segment.count.toLocaleString()}</td>
                <td>{segment.lastUpdated}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('marketing.audience.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    <button className="action-btn edit" title={t('marketing.audience.actions.useInCampaign')}>
                      <span className="material-icons">campaign</span>
                    </button>
                    <button className="action-btn delete" title={t('marketing.audience.actions.delete')}>
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEmailSmsBlasts = () => (
    <div className="tab-content">
      <h3>{t('marketing.emailSms.title')}</h3>
      <p>{t('marketing.emailSms.subtitle')}</p>
      
      <div className="blast-editor">
        <div className="editor-tabs">
          <button className="editor-tab active">{t('marketing.emailSms.editorTabs.emailEditor')}</button>
          <button className="editor-tab">{t('marketing.emailSms.editorTabs.smsEditor')}</button>
          <button className="editor-tab">{t('marketing.emailSms.editorTabs.templates')}</button>
        </div>
        
        <div className="editor-content">
          <div className="editor-sidebar">
            <h4>{t('marketing.emailSms.dynamicVariables')}</h4>
            <div className="variable-list">
              {['{{name}}', '{{level}}', '{{last_article_read}}', '{{subscription_plan}}', '{{school_name}}'].map(variable => (
                <button key={variable} className="variable-btn">{variable}</button>
              ))}
            </div>
            
            <h4>{t('marketing.emailSms.quickActions')}</h4>
            <div className="quick-actions">
              <button className="action-btn">{t('marketing.emailSms.quickActionButtons.testSend')}</button>
              <button className="action-btn">{t('marketing.emailSms.quickActionButtons.abTest')}</button>
              <button className="action-btn">{t('marketing.emailSms.quickActionButtons.schedule')}</button>
              <button className="action-btn">{t('marketing.emailSms.quickActionButtons.saveTemplate')}</button>
            </div>
          </div>
          
          <div className="editor-main">
            <div className="email-preview">
              <div className="preview-header">
                <input placeholder={t('marketing.emailSms.preview.subjectLine')} className="subject-input" />
                <div className="preview-controls">
                  <button className="preview-btn">{t('marketing.emailSms.preview.desktop')}</button>
                  <button className="preview-btn active">{t('marketing.emailSms.preview.mobile')}</button>
                </div>
              </div>
              <div className="preview-content">
                <div className="email-body" contentEditable>
                  <h2>Hello [User Name]!</h2>
                  <p>We noticed you're at [Level] level and haven't read an article in a while.</p>
                  <p>Here's a new article we think you'll love:</p>
                  <div className="cta-button">
                    <a href="#">Read Now</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCouponsOffers = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('marketing.coupons.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button 
          className="add-user-btn"
          onClick={() => setShowCreateCouponModal(true)}
        >
          <span className="material-icons">add</span>
          {t('marketing.coupons.createCoupon')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('marketing.coupons.tableHeaders.id')}</th>
              <th>{t('marketing.coupons.tableHeaders.couponCode')}</th>
              <th>{t('marketing.coupons.tableHeaders.discount')}</th>
              <th>{t('marketing.coupons.tableHeaders.expires')}</th>
              <th>{t('marketing.coupons.tableHeaders.uses')}</th>
              <th>{t('marketing.coupons.tableHeaders.status')}</th>
              <th>{t('marketing.coupons.tableHeaders.performance')}</th>
              <th>{t('marketing.coupons.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => (
              <tr key={coupon.id}>
                <td>{coupon.id}</td>
                <td>{coupon.code}</td>
                <td>
                  <span className="discount-badge">
                    {coupon.discountType === 'percentage' ? `${coupon.discountValue}% off` : coupon.discountValue}
                  </span>
                </td>
                <td>{coupon.expiresAt}</td>
                <td>
                  {coupon.uses}/{coupon.maxUses === -1 ? '∞' : coupon.maxUses}
                </td>
                <td>
                  <span className={`status-badge ${coupon.status}`}>
                    {coupon.status === 'active' ? t('marketing.coupons.status.active') : t('marketing.coupons.status.inactive')}
                  </span>
                </td>
                <td>
                  <div className="performance-metrics">
                    <span>{t('marketing.coupons.performance.redemptions')}: {coupon.redemptions}</span>
                    <span>{t('marketing.coupons.performance.conv')}: {coupon.conversionRate}%</span>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('marketing.coupons.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    <button className="action-btn edit" title={t('marketing.coupons.actions.duplicate')}>
                      <span className="material-icons">content_copy</span>
                    </button>
                    <button className="action-btn delete" title={t('marketing.coupons.actions.deactivate')}>
                      <span className="material-icons">block</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCampaignAnalytics = () => (
    <div className="tab-content">
      <h3>{t('marketing.analytics.title')}</h3>
      <p>{t('marketing.analytics.subtitle')}</p>
      
      <div className="analytics-overview">
        <div className="metric-card">
          <h4>{t('marketing.analytics.metrics.totalCampaigns')}</h4>
          <div className="metric-value">{analytics.totalCampaigns}</div>
          <div className="metric-change positive">+2 this month</div>
        </div>
        <div className="metric-card">
          <h4>{t('marketing.analytics.metrics.totalReach')}</h4>
          <div className="metric-value">{analytics.totalReach.toLocaleString()}</div>
          <div className="metric-change positive">+12% vs last month</div>
        </div>
        <div className="metric-card">
          <h4>{t('marketing.analytics.metrics.avgOpenRate')}</h4>
          <div className="metric-value">{analytics.avgOpenRate}%</div>
          <div className="metric-change positive">+2.1% vs last month</div>
        </div>
        <div className="metric-card">
          <h4>{t('marketing.analytics.metrics.avgClickRate')}</h4>
          <div className="metric-value">{analytics.avgClickRate}%</div>
          <div className="metric-change negative">-0.5% vs last month</div>
        </div>
      </div>
      
      <div className="analytics-charts">
        <div className="chart-container">
          <h4>{t('marketing.analytics.charts.campaignPerformance')}</h4>
          <div className="chart-placeholder">
            <div className="chart-line"></div>
            <div className="chart-line"></div>
            <div className="chart-line"></div>
            <div className="chart-legend">
              <span>{t('marketing.analytics.charts.openRate')}</span>
              <span>{t('marketing.analytics.charts.clickRate')}</span>
              <span>{t('marketing.analytics.charts.conversionRate')}</span>
            </div>
          </div>
        </div>
        
        <div className="chart-container">
          <h4>{t('marketing.analytics.charts.deviceBreakdown')}</h4>
          <div className="device-chart">
            <div className="device-segment mobile" style={{width: `${analytics.deviceBreakdown.mobile}%`}}>
              Mobile {analytics.deviceBreakdown.mobile}%
            </div>
            <div className="device-segment desktop" style={{width: `${analytics.deviceBreakdown.desktop}%`}}>
              Desktop {analytics.deviceBreakdown.desktop}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAutomationRules = () => (
    <div className="tab-content">
      <div className="user-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('marketing.automation.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-icons">search</span>
        </div>
        
        <button 
          className="add-user-btn"
          onClick={() => setShowCreateAutomationModal(true)}
        >
          <span className="material-icons">add</span>
          {t('marketing.automation.createRule')}
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('marketing.automation.tableHeaders.id')}</th>
              <th>{t('marketing.automation.tableHeaders.ruleName')}</th>
              <th>{t('marketing.automation.tableHeaders.trigger')}</th>
              <th>{t('marketing.automation.tableHeaders.actions')}</th>
              <th>{t('marketing.automation.tableHeaders.status')}</th>
              <th>{t('marketing.automation.tableHeaders.lastTriggered')}</th>
              <th>{t('marketing.automation.tableHeaders.totalTriggers')}</th>
              <th>{t('marketing.automation.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {automationRules.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.id}</td>
                <td>{rule.name}</td>
                <td>{rule.trigger.replace(/_/g, ' ')}</td>
                <td>
                  <div className="automation-actions">
                    {rule.actions.map((action, index) => (
                      <span key={index} className="action-badge">{action.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${rule.status}`}>
                    {translateCampaignStatus(rule.status)}
                  </span>
                </td>
                <td>{rule.lastTriggered}</td>
                <td>{rule.totalTriggers}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" title={t('marketing.campaigns.actions.edit')}>
                      <span className="material-icons">edit</span>
                    </button>
                    <button className="action-btn edit" title={t('marketing.campaigns.actions.duplicate')}>
                      <span className="material-icons">content_copy</span>
                    </button>
                    <button className="action-btn delete" title={t('marketing.campaigns.actions.delete')}>
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'campaign-manager':
        return renderCampaignManager();
      case 'audience-targeting':
        return renderAudienceTargeting();
      case 'email-sms-blasts':
        return renderEmailSmsBlasts();
      case 'coupons-offers':
        return renderCouponsOffers();
      case 'campaign-analytics':
        return renderCampaignAnalytics();
      case 'automation-rules':
        return renderAutomationRules();
      default:
        return renderCampaignManager();
    }
  };

  return (
    <div id="marketing-promotion">
      <div className="section">
        <div className="user-management-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        
        {renderTabContent()}
      </div>

      {/* Campaign Creation Modal */}
      {showCreateCampaignModal && (
        <div className="user-form-overlay" onClick={() => setShowCreateCampaignModal(false)}>
          <div className="user-form" onClick={(e) => e.stopPropagation()}>
            <h3>{t('marketing.campaigns.modals.createNewCampaign')}</h3>
            <input
              type="text"
              placeholder={t('marketing.campaigns.modals.campaignTitle')}
              value={newCampaign.title}
              onChange={(e) => setNewCampaign({...newCampaign, title: e.target.value})}
            />
            <textarea
              placeholder={t('marketing.campaigns.modals.campaignDescription')}
              value={newCampaign.description}
              onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
              rows="3"
            />
            <select
              value={newCampaign.type}
              onChange={(e) => setNewCampaign({...newCampaign, type: e.target.value})}
            >
              <option value="email">{t('marketing.campaigns.modals.email')}</option>
              <option value="sms">{t('marketing.campaigns.modals.sms')}</option>
              <option value="push">{t('marketing.campaigns.modals.pushNotification')}</option>
              <option value="in-app">{t('marketing.campaigns.modals.inAppBanner')}</option>
            </select>
            <textarea
              placeholder={t('marketing.campaigns.modals.campaignContent')}
              value={newCampaign.content}
              onChange={(e) => setNewCampaign({...newCampaign, content: e.target.value})}
              rows="6"
            />
            <div className="form-buttons">
              <button onClick={() => setShowCreateCampaignModal(false)}>{t('marketing.campaigns.modals.cancel')}</button>
              <button onClick={handleCreateCampaign}>{t('marketing.campaigns.modals.create')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Creation Modal */}
      {showCreateCouponModal && (
        <div className="user-form-overlay" onClick={() => setShowCreateCouponModal(false)}>
          <div className="user-form" onClick={(e) => e.stopPropagation()}>
            <h3>{t('marketing.coupons.createNewCoupon')}</h3>
            <input
              type="text"
              placeholder={t('marketing.coupons.couponCode')}
              value={newCoupon.code}
              onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
            />
            <select
              value={newCoupon.discountType}
              onChange={(e) => setNewCoupon({...newCoupon, discountType: e.target.value})}
            >
              <option value="percentage">{t('marketing.coupons.discountTypes.percentageOff')}</option>
              <option value="fixed">{t('marketing.coupons.discountTypes.fixedAmountOff')}</option>
              <option value="feature">{t('marketing.coupons.discountTypes.bonusFeature')}</option>
            </select>
            <input
              type="text"
              placeholder={t('marketing.coupons.discountValue')}
              value={newCoupon.discountValue}
              onChange={(e) => setNewCoupon({...newCoupon, discountValue: e.target.value})}
            />
            <input
              type="date"
              placeholder={t('marketing.coupons.expiresAt')}
              value={newCoupon.expiresAt}
              onChange={(e) => setNewCoupon({...newCoupon, expiresAt: e.target.value})}
            />
            <div className="form-buttons">
              <button onClick={() => setShowCreateCouponModal(false)}>{t('marketing.coupons.cancel')}</button>
              <button onClick={handleCreateCoupon}>{t('marketing.coupons.createCoupon')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingPromotion; 